import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from 'react';
import './style.css';
import {Divider} from 'antd';
import {
  attendingDinnerLabel,
  calculateProcessingFeeForRegistrations,
  formatCurrencyByLocalChapter,
  formatDate,
  formatUtcToLocalDateTime,
  getPaidAmountIncludingProcessingFee,
  getStatusInfoOfEventRegistration,
  meetingPresentationLabel,
  roundCurrency,
} from '../../../Util/commonUtility';

const resolveSponsorshipPerk = (rows = [], eventData) => {
  for (const row of rows) {
    const perk =
      row?.event_registration_options?.sponsorship?.event_sponsorship_perk ||
      row?.event_registration_options?.sponsorship?.eventSponsorshipPerk;
    if (perk) return perk;
  }
  for (const sponsorship of eventData?.sponsorships || []) {
    const perk =
      sponsorship?.event_sponsorship_perk ||
      sponsorship?.eventSponsorshipPerk ||
      sponsorship?.sponsor?.event_registration_options?.sponsorship
        ?.event_sponsorship_perk ||
      sponsorship?.sponsor?.event_registration_options?.sponsorship
        ?.eventSponsorshipPerk;
    if (perk) return perk;
  }
  return null;
};

import logo from '../../../Resource/Images/Logos/PBSS-logo.png';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import useLocalization from '../../../Localization';
import {ensureInterPdfFont} from './ensureInterPdfFont';

pdfMake.addVirtualFileSystem(
  (pdfFonts && pdfFonts['Roboto-Regular.ttf'] && pdfFonts) ||
    pdfFonts?.default ||
    pdfFonts?.pdfMake?.vfs ||
    pdfFonts?.vfs ||
    {},
);

const resolvePaymentForGroup = (payments, paymentGroup) => {
  if (!payments?.length) return null;
  const key = paymentGroup?.key;
  const keyMatches = (p) =>
    p &&
    p.transaction_key != null &&
    key != null &&
    String(p.transaction_key) === String(key);

  let p = payments.find(keyMatches);
  if (p) return p;

  const firstAttendeeTid = paymentGroup?.attendees?.[0]?.transaction_id;
  if (firstAttendeeTid != null && firstAttendeeTid !== '') {
    p = payments.find(
      (x) =>
        x &&
        x.transaction_key != null &&
        String(x.transaction_key) === String(firstAttendeeTid),
    );
    if (p) return p;
  }

  return null;
};

const sectionPayableAmount = (paymentGroup) => {
  if (!paymentGroup?.attendees?.length) return 0;
  return paymentGroup.attendees.reduce((sum, attendee) => {
    const totalCost = parseFloat(attendee?.total_cost || 0);
    const discount = parseFloat(attendee?.discount_amount || 0);
    return sum + (totalCost - discount);
  }, 0);
};

// Uses calculateProcessingFeeForRegistrations — callers must pass a matched
// payment only (never eventData as a stand-in).
const calculateProcessingFeeForGroup = (paymentGroup, payment) => {
  return calculateProcessingFeeForRegistrations(
    paymentGroup?.attendees || [],
    payment,
  );
};

const formatPaymentDateDisplay = (payment, paymentGroup, eventData) => {
  const payable = sectionPayableAmount(paymentGroup);

  const hasCredit = paymentGroup?.attendees?.some(
    (a) => Number(a?.credit_amount) > 0,
  );

  const isZeroAmount = Math.abs(payable) < 0.01 || hasCredit;
  if (isZeroAmount) {
    const registrationDate = paymentGroup?.attendees?.[0]?.registration_date;

    if (registrationDate) {
      return formatUtcToLocalDateTime(registrationDate);
    }
  }
  const raw = payment?.payment_datetime ?? payment?.payment_date;
  if (raw == null || raw === '') return null;
  return formatUtcToLocalDateTime(raw);
};

const categorizeAndGroupByTransitionId = (
  attendees,
  ApplicationString,
  registrationType,
) => {
  const filteredAttendees = attendees?.filter(
    (attendee) => attendee?.status !== 1,
  );
  const grouped = filteredAttendees?.reduce((acc, attendee) => {
    const transitionId = attendee?.transaction_id;
    const normalized = transitionId == null ? null : String(transitionId);
    const key =
      normalized && normalized !== 'null' && normalized !== 'undefined'
        ? normalized
        : 'null';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(attendee);
    return acc;
  }, {});

  const groups = Object.keys(grouped || {}).map((key) => {
    const rows = grouped[key];
    const createdAt = Math.min(
      ...rows.map((row) =>
        new Date(row?.created_at || row?.registration_date || 0).getTime(),
      ),
    );
    return {
      key,
      attendees: rows,
      isOriginal: rows.some((row) => row?.is_sponsor),
      createdAt: Number.isFinite(createdAt) ? createdAt : 0,
      transactionKey: key === 'null' ? '' : key,
    };
  });
  groups.sort((first, second) => {
    if (first.isOriginal !== second.isOriginal) {
      return first.isOriginal ? -1 : 1;
    }
    return first.createdAt - second.createdAt;
  });

  return groups.map((group, index) => ({
    ...group,
    label:
      registrationType === 'sponsorship'
        ? group.isOriginal
          ? ApplicationString['sponsorshipReps.originalPurchase']
          : ApplicationString['sponsorshipReps.addedReps']
        : `${ApplicationString['userEventDetailsModal.payment']} ${index + 1}`,
  }));
};

const yesNoLabel = (value) => (value ? 'Yes' : 'No');

const calculateTotals = (data) => {
  return data?.reduce(
    (totals, item) => {
      if (item?.status !== 1) {
        totals.totalCost += parseFloat(item?.total_cost || 0);
        totals.totalDiscountedPrice += parseFloat(item?.discount_amount || 0);
      }
      return totals;
    },
    {totalCost: 0, totalDiscountedPrice: 0},
  );
};

const JAPAN_LOCAL_CHAPTER_NAME = 'japan';

const formatAttendeeFieldOrNA = (value) => {
  const trimmed = value == null ? '' : String(value).trim();
  return trimmed && trimmed.toLowerCase() !== 'null' ? trimmed : 'N/A';
};

const safePdfText = (value, fallback = 'N/A') => {
  if (value === null || value === undefined) return fallback;
  const trimmed = String(value).trim();
  if (
    !trimmed ||
    trimmed.toLowerCase() === 'null' ||
    trimmed.toLowerCase() === 'undefined'
  ) {
    return fallback;
  }
  return trimmed;
};

const fetchImageAsDataUrl = async (imageUrl) => {
  const response = await fetch(imageUrl);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const ReceiptComponent = forwardRef(function ReceiptComponent(
  {
    eventData,
    eventLocalChapterData,
    eventRegistrationType,
    autoDownload = false,
    showDownloadButton = true,
    hideEventType = false,
    hideDownloadButton = false,
    onDownloadComplete,
    showTransactionKey = false,
  },
  ref,
) {
  const isJapanLocalChapter =
    eventLocalChapterData?.name?.toLowerCase() === JAPAN_LOCAL_CHAPTER_NAME;
  const [eventRegistrationTypeReceiptData, setEventRegistrationReceiptData] =
    useState([]);
  const {ApplicationString} = useLocalization();
  const [totalCost, setTotalCost] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalDiscount_amount, setDiscount_amount] = useState(0);
  const [totalProcessingFee, setTotalProcessingFee] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const autoDownloadTriggeredRef = useRef(false);

  const componentRef = useRef(null);

  const refundMode = (isBankRefund, refund_status) => {
    if (refund_status) {
      return isBankRefund ? 'PBSS Credit' : 'Refund to Bank';
    } else {
      return 'N/A';
    }
  };

  useEffect(() => {
    let receiptData = [];
    if (eventRegistrationType === 'sponsorship') {
      const {totalCost, totalDiscountedPrice} = calculateTotals(
        eventData?.sponsors,
      );
      receiptData = categorizeAndGroupByTransitionId(
        eventData?.sponsors,
        ApplicationString,
        eventRegistrationType,
      );
      setEventRegistrationReceiptData(receiptData);
      setTotalPrice(totalCost);
      setTotalCost(totalCost - totalDiscountedPrice);
      setDiscount_amount(totalDiscountedPrice);
    } else {
      const {totalCost, totalDiscountedPrice} = calculateTotals(
        eventData?.attendees,
      );
      receiptData = categorizeAndGroupByTransitionId(
        eventData?.attendees,
        ApplicationString,
        eventRegistrationType,
      );
      setEventRegistrationReceiptData(receiptData);
      setTotalPrice(totalCost);
      setTotalCost(totalCost - totalDiscountedPrice);
      setDiscount_amount(totalDiscountedPrice);
    }
    // Receipt total processing fee:
    // - Only use payments matched via resolvePaymentForGroup (transaction_key).
    // - Never fall back to eventData.processing_fee_amount — that value is the
    //   latest Stripe payment for the event/user and was previously applied to
    //   every section (including admin Payment Confirmed / Fee Waiver), then
    //   summed N times (e.g. 8 × $155.73 = $1,245.84).
    // - Dedupe by transaction_key so one Stripe charge cannot be counted twice.
    const seenPaymentKeys = new Set();
    const processingFee = (receiptData || []).reduce((sum, paymentGroup) => {
      const payment = resolvePaymentForGroup(eventData?.payments, paymentGroup);
      if (!payment) return sum;

      const paymentKey =
        payment.transaction_key != null
          ? String(payment.transaction_key)
          : null;
      if (paymentKey) {
        if (seenPaymentKeys.has(paymentKey)) return sum;
        seenPaymentKeys.add(paymentKey);
      }

      return sum + calculateProcessingFeeForGroup(paymentGroup, payment);
    }, 0);
    setTotalProcessingFee(roundCurrency(processingFee));
    setIsContentReady(true);
  }, [eventRegistrationType, eventData, ApplicationString]);

  const handleDownloadPDF = async () => {
    if (!isContentReady) {
      console.error(
        ApplicationString['receiptComponent.error.contentNotReady'],
      );
      return;
    }

    if (!eventData) {
      console.error(
        ApplicationString['receiptComponent.error.eventDataMissing'],
      );
      alert(ApplicationString['receiptComponent.error.eventDataMissing']);
      return;
    }

    setIsDownloading(true);

    try {
      const logoDataUrl = await fetchImageAsDataUrl(logo);
      await ensureInterPdfFont();
      const eventDate =
        formattedStartDate === formattedEndDate
          ? formattedStartDate
          : `${safePdfText(formattedStartDate)} to ${safePdfText(
              formattedEndDate,
            )}`;

      const content = [
        {
          columns: [
            {
              width: '*',
              stack: [
                {
                  text:
                    ApplicationString['receiptComponent.registrationReceipt'] ||
                    'Registration Receipt',
                  style: 'title',
                },
                {
                  text:
                    ApplicationString['receiptComponent.eventName'] ||
                    'Event Name',
                  style: 'sectionLabel',
                  margin: [0, 8, 0, 2],
                },
                {
                  text: safePdfText(eventData?.eventName),
                  style: 'eventName',
                },
              ],
            },
            {
              width: 150,
              stack: [
                {image: logoDataUrl, width: 70, alignment: 'right'},
                {
                  text:
                    ApplicationString['receiptComponent.logoText'] ||
                    'Pharmaceutical & BioScience Society',
                  alignment: 'right',
                  fontSize: 8,
                  margin: [0, 5, 0, 0],
                },
              ],
            },
          ],
        },
        {
          canvas: [
            {type: 'line', x1: 0, y1: 0, x2: 760, y2: 0, lineColor: '#d9d9d9'},
          ],
          margin: [0, 14, 0, 10],
        },
        {
          table: {
            widths: ['*', '*'],
            body: hideEventType
              ? [
                  [
                    {
                      text: [
                        {
                          text: `${
                            ApplicationString['receiptComponent.location'] ||
                            'Location'
                          }: `,
                          bold: true,
                        },
                        safePdfText(eventData?.location),
                      ],
                    },
                    {
                      text: [
                        {
                          text: `${
                            ApplicationString[
                              'receiptComponent.localChapterName'
                            ] || 'Local Chapter Name'
                          }: `,
                          bold: true,
                        },
                        safePdfText(eventData?.chapter),
                      ],
                    },
                  ],
                  [
                    {
                      text: [
                        {
                          text: `${
                            ApplicationString['receiptComponent.date'] || 'Date'
                          }: `,
                          bold: true,
                        },
                        safePdfText(eventDate),
                      ],
                    },
                    {text: ''},
                  ],
                ]
              : [
                  [
                    {
                      text: [
                        {
                          text: `${
                            ApplicationString['receiptComponent.eventType'] ||
                            'Event Type'
                          }: `,
                          bold: true,
                        },
                        safePdfText(eventData?.eventType),
                      ],
                    },
                    {
                      text: [
                        {
                          text: `${
                            ApplicationString['receiptComponent.location'] ||
                            'Location'
                          }: `,
                          bold: true,
                        },
                        safePdfText(eventData?.location),
                      ],
                    },
                  ],
                  [
                    {
                      text: [
                        {
                          text: `${
                            ApplicationString[
                              'receiptComponent.localChapterName'
                            ] || 'Local Chapter Name'
                          }: `,
                          bold: true,
                        },
                        safePdfText(eventData?.chapter),
                      ],
                    },
                    {
                      text: [
                        {
                          text: `${
                            ApplicationString['receiptComponent.date'] || 'Date'
                          }: `,
                          bold: true,
                        },
                        safePdfText(eventDate),
                      ],
                    },
                  ],
                ],
          },
          layout: 'noBorders',
          margin: [0, 0, 0, 14],
        },
      ];

      eventRegistrationTypeReceiptData?.forEach((paymentGroup, index) => {
        const sectionPaymentDetails = resolvePaymentForGroup(
          eventData?.payments,
          paymentGroup,
        );
        const sectionPaymentFormattedDate = formatPaymentDateDisplay(
          sectionPaymentDetails,
          paymentGroup,
          eventData,
        );
        const sectionMaskedCard = sectionPaymentDetails?.card_last4
          ? `**** **** **** ${sectionPaymentDetails.card_last4}`
          : null;
        const sectionProcessingFee = calculateProcessingFeeForGroup(
          paymentGroup,
          sectionPaymentDetails || eventData,
        );
        const paymentDateLabel =
          ApplicationString['receiptComponent.paymentDate'] || 'Payment Date';
        const cardLabel =
          ApplicationString['receiptComponent.cardLast4'] ||
          'Credit Card Number';
        const processingFeeLabel =
          ApplicationString['receiptComponent.processingFee'] ||
          'Processing Fee (2.9%)';
        const paymentMeta = [
          `${paymentDateLabel}: ${sectionPaymentFormattedDate || 'N/A'}`,
        ];

        if (sectionMaskedCard) {
          paymentMeta.push(`${cardLabel}: ${sectionMaskedCard}`);
        }

        if (sectionProcessingFee > 0) {
          paymentMeta.push(
            `${processingFeeLabel}: ${formatCurrencyByLocalChapter(
              sectionProcessingFee,
              eventLocalChapterData,
              2,
              2,
            )}`,
          );
        }

        // Meeting presentation / dinner / free lunch are sponsorship-only.
        const isSponsorshipReceipt =
          eventRegistrationType === 'sponsorship';

        const headers = [
          ApplicationString['receiptComponent.name'] || 'Name',
          ApplicationString['receiptComponent.email'] || 'Email',
          ApplicationString['receiptComponent.company'] || 'Company',
        ];
        // Prefer compact widths; Yes/No perk cols stay narrow so Status + Paid fit.
        const preferredWidths = [52, 88, 58];

        if (isJapanLocalChapter) {
          headers.push(
            ApplicationString['receiptComponent.department'] || 'Department',
            ApplicationString['receiptComponent.jobTitle'] || 'Job Title',
          );
          preferredWidths.push(42, 42);
        }

        headers.push(
          ApplicationString['receiptComponent.refundedAmount'] ||
            'Refunded Amount',
          ApplicationString['receiptComponent.refundType'] || 'Refund Type',
          ApplicationString['receiptComponent.registrationOptions'] ||
            'Registration Options',
          ApplicationString['receiptComponent.foodOptions'] || 'Food Options',
        );
        preferredWidths.push(42, 40, 62, 48);

        if (isSponsorshipReceipt) {
          const perk = resolveSponsorshipPerk(
            paymentGroup?.attendees,
            eventData,
          );
          const presentationPrice = Number(perk?.attendees_price || 0).toFixed(
            2,
          );
          const dinnerPrice = Number(
            perk?.dinner_with_speaker_price || 0,
          ).toFixed(2);
          // Full labels — never use shortened "Meeting @ / Dinner @" PDF headers.
          headers.push(
            meetingPresentationLabel(perk?.attendees_price, ApplicationString) ||
              `Attending Meeting Presentation at $${presentationPrice}?`,
            attendingDinnerLabel(
              perk?.dinner_with_speaker_price,
              ApplicationString,
            ) || `Attending Dinner at $${dinnerPrice}?`,
            ApplicationString['sponsorshipReps.freeLunch'] || 'Free lunch',
          );
          preferredWidths.push(110, 88, 42);
        }

        if (eventData.is_available_for_webcast) {
          headers.push(
            ApplicationString['receiptComponent.attendanceType'] ||
              'Attendance Type',
          );
          preferredWidths.push(42);
        }

        headers.push(
          ApplicationString['receiptComponent.status'] || 'Status',
          ApplicationString['receiptComponent.totalCost'] || 'Paid',
        );
        preferredWidths.push(40, 42);
        if (showTransactionKey) {
          headers.push('Transaction Key');
          preferredWidths.push(56);
        }

        const tableBody = [
          headers.map((header) => ({
            text: header,
            style: 'tableHeader',
          })),
          ...(paymentGroup?.attendees || []).map((item) => {
            const refundModeValue = item?.refund_to_account;
            const refundStatus = item?.status == 0;
            const statusInfo = getStatusInfoOfEventRegistration(
              item?.status,
              ApplicationString,
            );
            const attendeeName = item?.attendee_first_name
              ? `${item?.attendee_first_name} ${item?.attendee_last_name || ''}`
              : 'N/A';
            const row = [
              safePdfText(attendeeName),
              safePdfText(item?.attendee_email),
              safePdfText(item?.attendee_company),
            ];

            if (isJapanLocalChapter) {
              row.push(
                safePdfText(item?.attendee_department),
                safePdfText(item?.attendee_job_title),
              );
            }

            row.push(
              safePdfText(
                formatCurrencyByLocalChapter(
                  item?.refunded_amount,
                  eventLocalChapterData,
                ),
              ),
              safePdfText(refundMode(refundModeValue, refundStatus)),
              safePdfText(
                item?.event_registration_options?.registration?.name ||
                  item?.event_registration_options?.sponsorship?.name,
              ),
              safePdfText(item?.event_registration_options?.food?.name),
            );

            if (isSponsorshipReceipt) {
              const foodName = item?.event_registration_options?.food?.name;
              const foodPrice = item?.event_registration_options?.food?.price;
              row.push(
                yesNoLabel(item?.attending_presentation),
                yesNoLabel(item?.attending_dinner),
                yesNoLabel(
                  foodName &&
                    foodName !== 'No Food' &&
                    (foodPrice === 0 || foodPrice === '0'),
                ),
              );
            }

            if (eventData.is_available_for_webcast) {
              row.push(safePdfText(item.registration_type));
            }

            row.push(
              {
                text: safePdfText(statusInfo?.label),
                color: statusInfo?.color || '#000000',
              },
              safePdfText(
                formatCurrencyByLocalChapter(
                  getPaidAmountIncludingProcessingFee(
                    item,
                    sectionPaymentDetails || null,
                  ),
                  eventLocalChapterData,
                  2,
                  2,
                ),
              ),
            );
            if (showTransactionKey) {
              row.push(safePdfText(paymentGroup.transactionKey));
            }

            return row;
          }),
        ];

        // pdfMake column widths are content-only; cell padding/borders add width on top.
        // Fit sum(widths) so Status + Paid are never clipped on landscape A4.
        const pageContentWidth = 841.89 - 16 - 16; // landscape A4 − tighter margins
        const cellPadX = 2;
        const colCount = preferredWidths.length;
        const availableTableWidth = Math.max(
          200,
          pageContentWidth - colCount * (cellPadX * 2) - colCount,
        );
        const preferredSum = preferredWidths.reduce((sum, w) => sum + w, 0) || 1;
        const tableWidths = preferredWidths.map(
          (w) =>
            Math.round(((w / preferredSum) * availableTableWidth) * 100) / 100,
        );

        content.push(
          {
            text: `${
              paymentGroup.label || `Payment ${index + 1}`
            } (${paymentMeta.join(' - ')})`,
            style: 'paymentTitle',
            margin: [0, index === 0 ? 4 : 14, 0, 6],
          },
          {
            table: {
              headerRows: 1,
              widths: tableWidths,
              body: tableBody,
              dontBreakRows: true,
            },
            layout: {
              fillColor: (rowIndex) => (rowIndex === 0 ? '#f2f4f7' : null),
              hLineColor: () => '#d9d9d9',
              vLineColor: () => '#d9d9d9',
              paddingLeft: () => cellPadX,
              paddingRight: () => cellPadX,
              paddingTop: () => 3,
              paddingBottom: () => 3,
            },
          },
        );
      });

      const paymentDetailsBody = [
        [
          ApplicationString['receiptComponent.price'] || 'Price',
          {
            text: formatCurrencyByLocalChapter(
              totalPrice,
              eventLocalChapterData,
            ),
            alignment: 'right',
          },
        ],
        [
          ApplicationString['receiptComponent.discount'] || 'Discount',
          {
            text: formatCurrencyByLocalChapter(
              totalDiscount_amount,
              eventLocalChapterData,
            ),
            alignment: 'right',
          },
        ],
      ];

      if (totalProcessingFee > 0) {
        paymentDetailsBody.push([
          ApplicationString['receiptComponent.processingFee'] ||
            'Processing Fee (2.9%)',
          {
            text: formatCurrencyByLocalChapter(
              totalProcessingFee,
              eventLocalChapterData,
              2,
              2,
            ),
            alignment: 'right',
          },
        ]);
      }

      paymentDetailsBody.push([
        {
          text: ApplicationString['receiptComponent.total'] || 'Paid amount',
          bold: true,
        },
        {
          text: formatCurrencyByLocalChapter(
            totalCost + totalProcessingFee,
            eventLocalChapterData,
            2,
            2,
          ),
          bold: true,
          alignment: 'right',
        },
      ]);

      content.push(
        {
          text:
            ApplicationString['receiptComponent.paymentDetails'] ||
            'Payment Details',
          style: 'paymentTitle',
          margin: [0, 16, 0, 6],
        },
        {
          table: {
            widths: ['*', 110],
            body: paymentDetailsBody,
          },
          layout: {
            hLineColor: () => '#d9d9d9',
            vLineColor: () => '#d9d9d9',
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 5,
            paddingBottom: () => 5,
          },
        },
      );

      if (totalProcessingFee > 0) {
        content.push({
          text:
            ApplicationString['receiptComponent.processingFeeNote'] ||
            'Note: Processing fees are not refundable.',
          fontSize: 8,
          italics: true,
          margin: [0, 8, 0, 0],
        });
      }

      const docDefinition = {
        pageSize: 'A4',
        pageOrientation: 'landscape',
        // Tighter side margins so wide sponsorship tables keep Status + Paid on-page.
        pageMargins: [16, 22, 16, 22],
        defaultStyle: {
          font: 'Inter',
          fontSize: 7,
        },
        content,
        styles: {
          title: {
            fontSize: 18,
            bold: true,
          },
          sectionLabel: {
            fontSize: 11,
            bold: true,
          },
          eventName: {
            fontSize: 10,
          },
          paymentTitle: {
            fontSize: 10,
            bold: true,
          },
          tableHeader: {
            bold: true,
            fontSize: 6,
          },
        },
      };

      const pdfBlob = await new Promise((resolve) => {
        pdfMake.createPdf(docDefinition).getBlob(resolve);
      });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = pdfUrl;
      downloadLink.download = `event-receipt-${eventData.id || 'download'}.pdf`;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      // Try to open in new tab first
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <title>Event Receipt</title>
            </head>
            <body style="margin:0;padding:0;">
              <embed width="100%" height="100%" src="${pdfUrl}" type="application/pdf">
            </body>
          </html>
        `);
        newWindow.document.close();
      } else {
        console.log('Popup blocked, providing direct download...');
        const downloadLink = document.createElement('a');
        downloadLink.href = pdfUrl;
        downloadLink.download = `event-receipt-${
          eventData.id || 'download'
        }.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsDownloading(false);
      if (typeof onDownloadComplete === 'function') {
        onDownloadComplete();
      }
    }
  };

  useEffect(() => {
    if (
      !autoDownload ||
      !isContentReady ||
      !eventData ||
      autoDownloadTriggeredRef.current
    ) {
      return;
    }
    autoDownloadTriggeredRef.current = true;
    handleDownloadPDF();
  }, [autoDownload, isContentReady, eventData]);

  // Allow parents to trigger a direct download (e.g. the Registration History table's
  // receipt icon) without rendering a visible modal.
  useImperativeHandle(
    ref,
    () => ({
      downloadPDF: handleDownloadPDF,
      isReady: isContentReady,
    }),
    [isContentReady, eventData, eventRegistrationType],
  );

  const startDateValue =
    eventData?.dateObject?.startDate ||
    eventData?.event_start_date_time ||
    eventData?.event_start_date;
  const endDateValue =
    eventData?.dateObject?.endDate ||
    eventData?.event_end_date_time ||
    eventData?.event_end_date;

  const formattedStartDate = startDateValue ? formatDate(startDateValue) : null;
  const formattedEndDate = endDateValue ? formatDate(endDateValue) : null;

  return (
    <React.Fragment>
      <div ref={componentRef} className='my-receipt' style={{display: 'none'}}>
        <div className='receipt-header-block'>
          <div className='receipt-header-row'>
            <div className='receipt-header-left'>
              <h2 className='receipt-title'>
                {ApplicationString['receiptComponent.registrationReceipt']}
              </h2>
              <h3 className='receipt-event-name'>
                {ApplicationString['receiptComponent.eventName']}
              </h3>
              <div className='receipt-event-subtitle'>
                {eventData?.eventName}
              </div>
            </div>
            <div className='receipt-header-logo'>
              <img className='receipt-logo' src={logo} alt='PBSS Logo' />
              <div className='receipt-logo-text'>
                {ApplicationString['receiptComponent.logoText']}
              </div>
            </div>
          </div>
          <Divider />
          <div className='event-details-table-container'>
            <table className='event-details-table'>
              <tbody>
                {hideEventType ? (
                  <>
                    <tr>
                      <td>
                        <b>{ApplicationString['receiptComponent.location']}:</b>{' '}
                        {eventData?.location || 'N/A'}
                      </td>
                      <td>
                        <b>
                          {
                            ApplicationString[
                              'receiptComponent.localChapterName'
                            ]
                          }
                          :
                        </b>{' '}
                        {eventData?.chapter || 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>{ApplicationString['receiptComponent.date']}:</b>{' '}
                        {formattedStartDate === formattedEndDate
                          ? formattedStartDate
                          : `${formattedStartDate} to ${formattedEndDate}`}
                      </td>
                      <td />
                    </tr>
                  </>
                ) : (
                  <>
                    <tr>
                      <td>
                        <b>
                          {ApplicationString['receiptComponent.eventType']}:
                        </b>{' '}
                        {eventData?.eventType || 'N/A'}
                      </td>
                      <td>
                        <b>{ApplicationString['receiptComponent.location']}:</b>{' '}
                        {eventData?.location || 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>
                          {
                            ApplicationString[
                              'receiptComponent.localChapterName'
                            ]
                          }
                          :
                        </b>{' '}
                        {eventData?.chapter || 'N/A'}
                      </td>
                      <td>
                        <b>{ApplicationString['receiptComponent.date']}:</b>{' '}
                        {formattedStartDate === formattedEndDate
                          ? formattedStartDate
                          : `${formattedStartDate} to ${formattedEndDate}`}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {eventRegistrationTypeReceiptData?.map((paymentGroup, index) => {
          const sectionPaymentDetails = resolvePaymentForGroup(
            eventData?.payments,
            paymentGroup,
          );
          const sectionPaymentFormattedDate = formatPaymentDateDisplay(
            sectionPaymentDetails,
            paymentGroup,
            eventData,
          );
          const sectionMaskedCard = sectionPaymentDetails?.card_last4
            ? `**** **** **** ${sectionPaymentDetails.card_last4}`
            : null;
          // Section fee only when this group maps to a real payments row AND the
          // attendees were actually charged (paid_amount / Paid status). Do not use
          // `sectionPaymentDetails || eventData` — that inherited another checkout's
          // fee onto admin Payment Confirmed / Fee Waiver (Payment Date N/A) groups.
          // Also: a leftover Stripe PI on an unpaid Payment Confirmed row must not
          // produce fee from total_cost (admin Paid Amount stays $0 in that case).
          const sectionProcessingFee = sectionPaymentDetails
            ? calculateProcessingFeeForGroup(
                paymentGroup,
                sectionPaymentDetails,
              )
            : 0;
          const paymentDateLabel =
            ApplicationString['receiptComponent.paymentDate'] || 'Payment Date';
          const cardLabel =
            ApplicationString['receiptComponent.cardLast4'] ||
            'Credit Card Number';
          const processingFeeLabel =
            ApplicationString['receiptComponent.processingFee'] ||
            'Processing Fee (2.9%)';

          return (
            <div key={paymentGroup.key} className='payment-section'>
              <div className='payment-section-header'>
                <h4 className='payment-label'>
                  {paymentGroup.label || `Payment ${index + 1}`}
                  <span className='payment-meta-light'>
                    {' '}
                    ({paymentDateLabel}: {sectionPaymentFormattedDate || 'N/A'}
                    {sectionMaskedCard ? (
                      <>
                        {' '}
                        · {cardLabel}: {sectionMaskedCard}
                      </>
                    ) : null}
                    {sectionProcessingFee > 0 ? (
                      <>
                        {' '}
                        · {processingFeeLabel}:{' '}
                        {formatCurrencyByLocalChapter(
                          sectionProcessingFee,
                          eventLocalChapterData,
                          2,
                          2,
                        )}
                      </>
                    ) : null}
                    )
                  </span>
                </h4>
              </div>
              <table className='payment-table'>
                <thead>
                  <tr>
                    <th>{ApplicationString['receiptComponent.name']}</th>
                    <th>{ApplicationString['receiptComponent.email']}</th>
                    <th>{ApplicationString['receiptComponent.company']}</th>
                    {isJapanLocalChapter && (
                      <>
                        <th>
                          {ApplicationString['receiptComponent.department']}
                        </th>
                        <th>
                          {ApplicationString['receiptComponent.jobTitle']}
                        </th>
                      </>
                    )}
                    <th>
                      {ApplicationString['receiptComponent.refundedAmount']}
                    </th>
                    <th>{ApplicationString['receiptComponent.refundType']}</th>
                    <th>
                      {
                        ApplicationString[
                          'receiptComponent.registrationOptions'
                        ]
                      }
                    </th>
                    <th>{ApplicationString['receiptComponent.foodOptions']}</th>
                    {eventRegistrationType === 'sponsorship' && (
                      <>
                        <th>
                          {meetingPresentationLabel(
                            resolveSponsorshipPerk(
                              paymentGroup?.attendees,
                              eventData,
                            )?.attendees_price,
                            ApplicationString,
                          )}
                        </th>
                        <th>
                          {attendingDinnerLabel(
                            resolveSponsorshipPerk(
                              paymentGroup?.attendees,
                              eventData,
                            )?.dinner_with_speaker_price,
                            ApplicationString,
                          )}
                        </th>
                        <th>
                          {ApplicationString['sponsorshipReps.freeLunch'] ||
                            'Free lunch'}
                        </th>
                      </>
                    )}
                    {eventData.is_available_for_webcast && (
                      <th>
                        {ApplicationString['receiptComponent.attendanceType']}
                      </th>
                    )}

                    <th>{ApplicationString['receiptComponent.status']}</th>
                    <th>{ApplicationString['receiptComponent.totalCost']}</th>
                    {showTransactionKey && <th>Transaction Key</th>}
                  </tr>
                </thead>
                <tbody>
                  {paymentGroup?.attendees?.map((item) => {
                    const refund_mode = item?.refund_to_account;
                    const refund_status = item?.status == 0;
                    const foodName =
                      item?.event_registration_options?.food?.name;
                    const foodPrice =
                      item?.event_registration_options?.food?.price;
                    return (
                      <tr key={item?.id}>
                        <td>
                          {item?.attendee_first_name
                            ? item?.attendee_first_name +
                              ' ' +
                              item?.attendee_last_name
                            : 'N/A'}
                        </td>
                        <td>{item?.attendee_email || 'N/A'}</td>
                        <td>{item?.attendee_company || 'N/A'}</td>
                        {isJapanLocalChapter && (
                          <>
                            <td>
                              {formatAttendeeFieldOrNA(
                                item?.attendee_department,
                              )}
                            </td>
                            <td>
                              {formatAttendeeFieldOrNA(
                                item?.attendee_job_title,
                              )}
                            </td>
                          </>
                        )}
                        <td>
                          {formatCurrencyByLocalChapter(
                            item?.refunded_amount,
                            eventLocalChapterData,
                          ) || 'N/A'}
                        </td>
                        <td>{refundMode(refund_mode, refund_status)}</td>
                        <td>
                          {item?.event_registration_options?.registration
                            ?.name ||
                            item?.event_registration_options?.sponsorship
                              ?.name ||
                            'N/A'}
                        </td>
                        <td>{foodName || 'N/A'}</td>
                        {eventRegistrationType === 'sponsorship' && (
                          <>
                            <td>
                              {yesNoLabel(item?.attending_presentation)}
                            </td>
                            <td>{yesNoLabel(item?.attending_dinner)}</td>
                            <td>
                              {yesNoLabel(
                                foodName &&
                                  foodName !== 'No Food' &&
                                  (foodPrice === 0 || foodPrice === '0'),
                              )}
                            </td>
                          </>
                        )}
                        {eventData.is_available_for_webcast && (
                          <td>{item.registration_type || 'N/A'}</td>
                        )}
                        <td
                          style={{
                            color: getStatusInfoOfEventRegistration(
                              item?.status,
                              ApplicationString,
                            )?.color,
                          }}>
                          {
                            getStatusInfoOfEventRegistration(
                              item?.status,
                              ApplicationString,
                            )?.label
                          }
                        </td>
                        <td>
                          {formatCurrencyByLocalChapter(
                            getPaidAmountIncludingProcessingFee(
                              item,
                              sectionPaymentDetails,
                            ),
                            eventLocalChapterData,
                            2,
                            2,
                          ) || 'N/A'}
                        </td>
                        {showTransactionKey && (
                          <td>{paymentGroup.transactionKey || 'N/A'}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
        <div className='payment-details-section'>
          <h4>{ApplicationString['receiptComponent.paymentDetails']}</h4>
          <table className='payment-details-table'>
            <tbody>
              <tr>
                <td>{ApplicationString['receiptComponent.price']}</td>
                <td className='align-right'>
                  {formatCurrencyByLocalChapter(
                    totalPrice,
                    eventLocalChapterData,
                  )}
                </td>
              </tr>
              <tr>
                <td>{ApplicationString['receiptComponent.discount']}</td>
                <td className='align-right'>
                  {formatCurrencyByLocalChapter(
                    totalDiscount_amount,
                    eventLocalChapterData,
                  )}
                </td>
              </tr>
              {totalProcessingFee > 0 && (
                <tr>
                  <td>{ApplicationString['receiptComponent.processingFee']}</td>
                  <td className='align-right'>
                    {formatCurrencyByLocalChapter(
                      totalProcessingFee,
                      eventLocalChapterData,
                      2,
                      2,
                    )}
                  </td>
                </tr>
              )}
              <tr className='total-row'>
                <td>{ApplicationString['receiptComponent.total']}</td>
                <td className='align-right'>
                  {formatCurrencyByLocalChapter(
                    totalCost + totalProcessingFee,
                    eventLocalChapterData,
                    2,
                    2,
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          {totalProcessingFee > 0 && (
            <p className='receipt-processing-fee-note'>
              {ApplicationString['receiptComponent.processingFeeNote']}
            </p>
          )}
        </div>
      </div>
      {showDownloadButton && !autoDownload && !hideDownloadButton ? (
        <button
          className='download-button'
          id={`myProfileMyEvents-download-button_${eventData?.id}`}
          onClick={handleDownloadPDF}
          disabled={isDownloading || !isContentReady}>
          <div className='docs'>
            {isDownloading
              ? ApplicationString['receiptComponent.downloading']
              : ApplicationString['receiptComponent.downloadReceipt']}
          </div>
          <div className='download'>
            <svg
              className='css-i6dzq1'
              strokeLinejoin='round'
              strokeLinecap='round'
              fill='none'
              strokeWidth='2'
              stroke='currentColor'
              height='24'
              width='24'
              viewBox='0 0 24 24'>
              <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'></path>
              <polyline points='7 10 12 15 17 10'></polyline>
              <line y2='3' x2='12' y1='15' x1='12'></line>
            </svg>
          </div>
        </button>
      ) : null}
    </React.Fragment>
  );
});

export default ReceiptComponent;
